import { useEffect, useRef, type ComponentType, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface PageTab { id: string; label: string; icon?: ComponentType<{ className?: string }> }

/**
 * Section tabs for pages that have several areas (Settings, Organization). The active tab is a
 * raised white tab joined to the white panel below it with curved corners; the rest sit on the
 * page background. Scrolls sideways on small screens, keeping the active tab in view.
 */
export default function PageTabs({ tabs, active, onChange, children, className }: {
  tabs: PageTab[];
  active: string;
  onChange: (id: string) => void;
  children: ReactNode;
  className?: string;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const first = tabs[0]?.id === active;

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [active]);

  const onKey = (e: React.KeyboardEvent) => {
    const i = tabs.findIndex((t) => t.id === active);
    const next = e.key === "ArrowRight" ? i + 1 : e.key === "ArrowLeft" ? i - 1 : null;
    if (next === null) return;
    e.preventDefault();
    const tab = tabs[(next + tabs.length) % tabs.length];
    onChange(tab.id);
    listRef.current?.querySelector<HTMLElement>(`[data-tab="${tab.id}"]`)?.focus();
  };

  return (
    <div className={cn("rounded-[1.75rem] bg-[#f3f4f8] p-1.5 sm:p-2", className)}>
      <div ref={listRef} role="tablist" onKeyDown={onKey} className="no-scrollbar flex items-end overflow-x-auto px-0.5 pt-0.5">
        {tabs.map((tab) => {
          const on = tab.id === active;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              data-tab={tab.id}
              aria-selected={on}
              tabIndex={on ? 0 : -1}
              onClick={() => onChange(tab.id)}
              className={cn(
                "relative flex shrink-0 items-center gap-2 whitespace-nowrap px-4 py-3 text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary sm:px-5",
                on ? "z-10 rounded-t-2xl bg-white font-semibold text-gray-900" : "rounded-2xl text-gray-500 hover:text-gray-900",
              )}
            >
              {Icon && <Icon className={cn("h-4 w-4", on ? "text-primary" : "text-gray-400")} />}
              {tab.label}
              {on && (
                <>
                  {/* Inverted corners that blend the tab into the panel. */}
                  {!first && <span aria-hidden className="pointer-events-none absolute -left-4 bottom-0 h-4 w-4 bg-[radial-gradient(circle_at_0_0,transparent_1rem,white_calc(1rem+0.5px))]" />}
                  <span aria-hidden className="pointer-events-none absolute -right-4 bottom-0 h-4 w-4 bg-[radial-gradient(circle_at_100%_0,transparent_1rem,white_calc(1rem+0.5px))]" />
                </>
              )}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" className={cn("rounded-3xl bg-white p-4 sm:p-6", first && "rounded-tl-none")}>
        {children}
      </div>
    </div>
  );
}
